import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Paper, Typography, LinearProgress } from '@mui/material';
import { alpha, keyframes } from '@mui/material/styles';

import { FONT_FAMILY, FONT_MONO, color, r } from '../../theme/tokens';

// Agent-style live activity panel for async jobs.
//
// Domain-neutral by design (docs/stack-generation/live-progress.md): the only
// job-specific input is the `progress` contract polled from /api/job_status —
// { stage, label, pct, done, total, events: [{ seq, type, text }] } — so any
// future pipeline or agent surface can reuse this component unchanged.
//
// Playback layer: the poll delivers snapshots every ~3s, but fast jobs finish
// whole stages between polls — rendering the latest snapshot directly would
// jump-cut (e.g. "0 of 20" straight to a later stage). Incoming stages and
// events are therefore queued and played back at reading speed: stages check
// off in order, event lines flow one at a time, and the counter climbs as
// evaluation events play. The queue drains faster when it backs up, so the
// display stays near-real-time on long runs and smooth on bursts.
//
// Layout follows the research-agent idiom (Perplexity-style): a thin progress
// strip, a left-aligned vertical step rail (completed checks above a pulsing,
// shimmering active step), and a scrollable activity feed nested under the
// active step that auto-follows like a terminal until the user scrolls up.
//
// With no `progress` (older backend, cold start, job not yet created) it
// degrades to the caller-provided fallback copy and animated percentage.

const MAX_FEED_EVENTS = 30;
// Playback pacing: normal cadence for readability, fast cadence to catch up
// when a poll delivers a large burst.
const FAST_DRAIN_QUEUE_LEN = 6;
const FAST_DRAIN_INTERVAL_MS = 120;
// How close to the bottom (px) still counts as "following" the feed.
const FEED_STICK_THRESHOLD_PX = 24;

const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 ${alpha(color.accent, 0.45)}; }
  70% { box-shadow: 0 0 0 0.4rem ${alpha(color.accent, 0)}; }
  100% { box-shadow: 0 0 0 0 ${alpha(color.accent, 0)}; }
`;

const textShimmer = keyframes`
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(0.25rem); }
  to { opacity: 1; transform: translateY(0); }
`;

// This panel is one of the two genuine code surfaces that keep a mono face —
// the documented exception to "Archivo everywhere" (DESIGN_DOC §10).
const MONO_STACK = FONT_MONO;
const SANS_STACK = FONT_FAMILY;

const CompletedDot = () => (
  <Box
    component="span"
    sx={{
      width: '1rem',
      height: '1rem',
      borderRadius: '50%',
      flexShrink: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: alpha(color.accent, 0.12),
      color: color.evergreen,
      fontSize: '0.6rem',
      fontWeight: 700,
      lineHeight: 1,
    }}
  >
    ✓
  </Box>
);

const ActiveDot = () => (
  <Box
    component="span"
    sx={{
      width: '1rem',
      height: '1rem',
      borderRadius: '50%',
      flexShrink: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}
  >
    <Box
      component="span"
      sx={{
        width: '0.5rem',
        height: '0.5rem',
        borderRadius: '50%',
        backgroundColor: color.accent,
        animation: `${pulse} 1.6s ease-out infinite`,
      }}
    />
  </Box>
);

export default function AgentActivityPanel({
  progress = null,
  fallbackLabel = 'Working…',
  fallbackPct = 0,
  resetKey = '',
  children = null,
  footer = null,
  // Milliseconds between played-back items. 0 = apply synchronously (tests).
  playbackIntervalMs = 350,
  // The job has finished and results are imminent — finish the remaining
  // playback at catch-up pace instead of reading pace.
  complete = false,
}) {
  // Displayed state — what playback has revealed so far, NOT the raw latest
  // server snapshot.
  const [stages, setStages] = useState([]);
  const [events, setEvents] = useState([]);
  // Displayed "n of m" counter. Climbs as evaluation events play back, then
  // reconciles to the server's count once the queue drains.
  const [counter, setCounter] = useState(null);
  const [displayPct, setDisplayPct] = useState(0);

  // Pending playback items: { kind: 'stage'|'event', ... }.
  const queueRef = useRef([]);
  const timerRef = useRef(null);
  const lastSeqRef = useRef(0);
  const seenStagesRef = useRef(new Set());
  // Latest server-truth counts, applied when playback catches up.
  const serverCountsRef = useRef(null);
  // Last non-null counts seen this job. On fast runs the server has often
  // moved past the counted stage (done/total back to null) before its events
  // play back — the climb must use the totals from when they were reported,
  // not the latest snapshot.
  const lastKnownCountsRef = useRef(null);
  const feedRef = useRef(null);
  // Whether the feed should auto-follow new lines (user hasn't scrolled up).
  const stickToBottomRef = useRef(true);
  // Mirrors the `complete` prop for timer callbacks (avoids stale closures).
  const completeRef = useRef(complete);
  completeRef.current = complete;

  const applyItem = useCallback((item) => {
    if (item.kind === 'stage') {
      setStages((prev) => (prev.some((s) => s.id === item.id) ? prev : [...prev, item]));
      // A new stage invalidates the previous stage's counter; evaluation
      // events (or reconciliation) rebuild it for the new stage.
      setCounter(null);
    } else {
      setEvents((prev) => [...prev, item.event].slice(-MAX_FEED_EVENTS));
      if (item.event.type === 'study') {
        const totals = lastKnownCountsRef.current;
        if (totals && totals.total > 0) {
          setCounter((c) => ({
            done: Math.min((c ? c.done : 0) + 1, totals.total),
            total: totals.total,
          }));
        }
      }
    }
  }, []);

  const reconcileCounter = useCallback(() => {
    // Server-truth wins once playback catches up, except it never moves the
    // displayed count backwards: intermediate server counts are throttled
    // (decorators/cache.py), so played-back events can legitimately be ahead.
    setCounter((current) => {
      const server = serverCountsRef.current;
      if (!server) return null;
      if (current && current.total === server.total && current.done > server.done) {
        return current;
      }
      return server;
    });
  }, []);

  const drainStep = useCallback(() => {
    const item = queueRef.current.shift();
    if (item) applyItem(item);
    if (queueRef.current.length > 0) {
      const interval = (completeRef.current || queueRef.current.length > FAST_DRAIN_QUEUE_LEN)
        ? FAST_DRAIN_INTERVAL_MS
        : playbackIntervalMs;
      timerRef.current = setTimeout(drainStep, interval);
    } else {
      timerRef.current = null;
      reconcileCounter();
    }
  }, [applyItem, reconcileCounter, playbackIntervalMs]);

  const ensureDraining = useCallback(() => {
    if (playbackIntervalMs === 0) {
      // Synchronous mode: apply everything immediately.
      while (queueRef.current.length > 0) applyItem(queueRef.current.shift());
      reconcileCounter();
      return;
    }
    if (!timerRef.current && queueRef.current.length > 0) {
      timerRef.current = setTimeout(drainStep, 0);
    }
  }, [playbackIntervalMs, applyItem, reconcileCounter, drainStep]);

  useEffect(() => {
    setStages([]);
    setEvents([]);
    setCounter(null);
    setDisplayPct(0);
    queueRef.current = [];
    lastSeqRef.current = 0;
    seenStagesRef.current = new Set();
    serverCountsRef.current = null;
    lastKnownCountsRef.current = null;
    stickToBottomRef.current = true;
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, [resetKey]);

  // Clear any pending playback timer on unmount.
  useEffect(() => () => {
    if (timerRef.current) clearTimeout(timerRef.current);
  }, []);

  useEffect(() => {
    if (!progress) return;

    serverCountsRef.current = (progress.total > 0 && progress.done != null)
      ? { done: progress.done, total: progress.total }
      : null;
    if (serverCountsRef.current) {
      lastKnownCountsRef.current = serverCountsRef.current;
    }

    // Stage history (progress.stages) covers stages too short for any poll to
    // observe directly; fall back to the single current stage for payloads
    // from older backends.
    const stageList = (Array.isArray(progress.stages) && progress.stages.length
      ? progress.stages.map((s) => ({ id: s.stage, label: s.label || s.stage }))
      : (progress.stage ? [{ id: progress.stage, label: progress.label || progress.stage }] : [])
    ).filter((s) => s.id && !seenStagesRef.current.has(s.id));
    stageList.forEach((s) => seenStagesRef.current.add(s.id));

    const incoming = (progress.events || [])
      .filter((e) => e && Number.isFinite(e.seq) && e.seq > lastSeqRef.current && e.text)
      // 'candidate' events are not rendered (bare names confused users); the
      // filter stays for back-compat with payloads from older backends.
      .filter((e) => e.type !== 'candidate')
      .sort((a, b) => a.seq - b.seq);
    if (incoming.length) {
      lastSeqRef.current = incoming[incoming.length - 1].seq;
    }

    // Narrative order within one poll: stages that already finished, then the
    // buffered events (they accumulated before the newest stage began), then
    // the newest stage. This keeps e.g. evaluation verdicts flowing under
    // "Evaluating…" even when the server has already moved on.
    stageList.slice(0, -1).forEach((s) => queueRef.current.push({ kind: 'stage', ...s }));
    incoming.forEach((e) => queueRef.current.push({ kind: 'event', event: e }));
    if (stageList.length) {
      queueRef.current.push({ kind: 'stage', ...stageList[stageList.length - 1] });
    }

    if (queueRef.current.length > 0) {
      ensureDraining();
    } else {
      reconcileCounter();
    }
  }, [progress, ensureDraining, reconcileCounter]);

  // The strip never moves backwards: real pct and the fallback animation race
  // forward together and the max so far wins.
  useEffect(() => {
    const candidate = Math.max(progress?.pct || 0, fallbackPct || 0);
    setDisplayPct((prev) => (candidate > prev ? candidate : prev));
  }, [progress, fallbackPct]);

  // Terminal-style follow: keep the newest line in view unless the user has
  // scrolled up to read history.
  useEffect(() => {
    const el = feedRef.current;
    if (el && stickToBottomRef.current) {
      el.scrollTop = el.scrollHeight;
    }
  }, [events]);

  const handleFeedScroll = (e) => {
    const el = e.currentTarget;
    stickToBottomRef.current =
      el.scrollHeight - el.scrollTop - el.clientHeight < FEED_STICK_THRESHOLD_PX;
  };

  const activeLabel = (stages.length ? stages[stages.length - 1].label : '') || fallbackLabel;
  const completedStages = stages.slice(0, -1);
  const hasCounter = counter && counter.total > 0 && counter.done != null;

  return (
    <Paper
      elevation={0}
      sx={{
        padding: 2.5,
        textAlign: 'left',
        borderRadius: '1rem',
        backgroundColor: color.paper,
        border: `0.0625rem solid ${alpha(color.ink, 0.16)}`,
        boxShadow: `0 0.625rem 1.75rem ${alpha(color.ink, 0.18)}, 0 0.125rem 0.5rem ${alpha(color.ink, 0.1)}`,
      }}
    >
      {/* Thin progress strip across the top of the card */}
      <LinearProgress
        variant="determinate"
        value={Math.min(displayPct, 100)}
        sx={{
          mb: 2,
          height: '0.125rem',
          borderRadius: '0.0625rem',
          backgroundColor: alpha(color.ink, 0.1),
          '& .MuiLinearProgress-bar': {
            background: `linear-gradient(90deg, ${color.accent} 0%, ${color.evergreen} 100%)`,
            borderRadius: '0.0625rem',
          },
        }}
      />

      {children}

      {/* Step rail: completed stages, then the shimmering active stage */}
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        {completedStages.map((stage) => (
          <Box key={stage.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.375 }}>
            <CompletedDot />
            <Typography
              sx={{ fontFamily: SANS_STACK, fontSize: '0.8rem', color: color.inkMuted }}
            >
              {stage.label}
            </Typography>
          </Box>
        ))}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.375, minHeight: '1.75rem' }}>
          <ActiveDot />
          <Typography
            sx={{
              fontFamily: SANS_STACK,
              fontSize: '0.9rem',
              fontWeight: 600,
              // Perplexity-style shimmer on the working step label.
              background: `linear-gradient(90deg, ${color.ink} 35%, ${color.inkMuted} 50%, ${color.ink} 65%)`,
              backgroundSize: '200% 100%',
              WebkitBackgroundClip: 'text',
              backgroundClip: 'text',
              color: 'transparent',
              animation: `${textShimmer} 2.4s linear infinite`,
            }}
          >
            {activeLabel}
          </Typography>
          {hasCounter && (
            <Typography
              component="span"
              sx={{
                ml: 0.5,
                px: 1,
                py: 0.125,
                borderRadius: r(999),
                backgroundColor: alpha(color.evergreen, 0.08),
                color: color.evergreen,
                fontFamily: MONO_STACK,
                fontSize: '0.68rem',
                fontWeight: 600,
                whiteSpace: 'nowrap',
              }}
            >
              {counter.done} of {counter.total}
            </Typography>
          )}
        </Box>

        {/* Activity nested under the active step, on a left rail. Scrollable
            with terminal-style auto-follow. */}
        {events.length > 0 && (
          <Box
            sx={{
              ml: '0.4375rem', // centers the rail under the 1rem dots
              pl: 1.75,
              borderLeft: `0.125rem solid ${alpha(color.ink, 0.1)}`,
              pt: 0.25,
              pb: 0.25,
            }}
          >
            <Box
              data-testid="agent-activity-feed"
              ref={feedRef}
              onScroll={handleFeedScroll}
              role="log"
              aria-live="polite"
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0.375,
                maxHeight: '6.75rem',
                overflowY: 'auto',
                // Subtle scrollbar that doesn't fight the rail aesthetic.
                scrollbarWidth: 'thin',
                '&::-webkit-scrollbar': { width: '0.25rem' },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: alpha(color.ink, 0.18),
                  borderRadius: '0.125rem',
                },
              }}
            >
              {events.map((event) => (
                <Typography
                  key={event.seq}
                  sx={{
                    fontFamily: MONO_STACK,
                    fontSize: '0.72rem',
                    lineHeight: 1.45,
                    color: color.inkSoft,
                    animation: `${fadeIn} 0.4s ease-out`,
                    overflowWrap: 'break-word',
                    flexShrink: 0,
                  }}
                >
                  {event.text}
                </Typography>
              ))}
            </Box>
          </Box>
        )}
      </Box>

      {footer}
    </Paper>
  );
}
